import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/format-api-error";
import { useMarkRequestSpent } from "./use-mark-request-spent";

// Sub-bucket 15 — port of /app/admin/mdf/requests/_components/MarkSpentDialog.tsx.
// Internal-accounting-only flip from approved → spent. The partner sees the
// status change on next refetch; no separate notification fires (legacy
// parity). HubSpot Note dispatch is fire-and-forget from the service.

export function MarkSpentDialog({
  requestId,
  fundId,
  open,
  onOpenChange,
}: {
  requestId: string;
  fundId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const markSpent = useMarkRequestSpent();

  function onConfirm() {
    markSpent.mutate(
      { id: requestId, fundId },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to mark as spent")),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Mark as spent?
          </DialogTitle>
          <DialogDescription>
            Confirms the partner has spent this budget. The partner sees the
            updated status on their next visit; no separate notification fires.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={markSpent.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={markSpent.isPending}
          >
            {markSpent.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Mark spent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
